const toCount = (value) => Math.max(0, Number(value || 0));

export const getQuestionViewCount = (question) => (
  Number.isFinite(Number(question?.viewCount))
    ? toCount(question.viewCount)
    : (question?.hasBeenSeen ? 1 : 0)
);

export const getQuestionsByType = (questions = [], quizType = "Closed") => {
  const questionList = Array.isArray(questions) ? questions : [];

  if (quizType === "Open") {
    return questionList.filter((question) => question.questionType === "open");
  }

  if (quizType === "Both") {
    return [...questionList];
  }

  return questionList.filter((question) => question.questionType === "closed");
};

export const getQuestionFilterAvailability = (questions = []) => {
  const questionList = Array.isArray(questions) ? questions : [];
  const unseen = questionList.filter((question) => getQuestionViewCount(question) === 0).length;
  const seen = questionList.length - unseen;

  return {
    all: questionList.length,
    unseen,
    seen,
    progressive: seen > 0 ? questionList.length : 0,
  };
};

export const filterQuestionsByView = (questions = [], filterValue = "all") => {
  const questionList = Array.isArray(questions) ? questions : [];

  if (filterValue === "unseen") {
    return questionList.filter((question) => getQuestionViewCount(question) === 0);
  }

  if (filterValue === "seen") {
    return questionList.filter((question) => getQuestionViewCount(question) > 0);
  }

  return [...questionList];
};

const shuffle = (items, random = Math.random) => {
  const result = [...items];

  for (let index = result.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(random() * (index + 1));
    [result[index], result[randomIndex]] = [result[randomIndex], result[index]];
  }

  return result;
};

export const orderQuestionsForQuiz = (
  questions = [],
  { filterValue = "all", order = "inserted", random = Math.random } = {}
) => {
  const questionList = Array.isArray(questions) ? questions : [];

  if (filterValue !== "progressive") {
    return order === "random" ? shuffle(questionList, random) : [...questionList];
  }

  const levels = questionList.reduce((groups, question) => {
    const viewCount = getQuestionViewCount(question);
    const level = groups.get(viewCount) || [];
    level.push(question);
    groups.set(viewCount, level);
    return groups;
  }, new Map());

  return [...levels.keys()]
    .sort((first, second) => first - second)
    .flatMap((level) => (
      order === "random" ? shuffle(levels.get(level), random) : levels.get(level)
    ));
};

export const selectQuestionsForQuiz = (
  questions = [],
  requestedCount,
  options = {}
) => {
  const uniqueQuestions = [];
  const seenIds = new Set();

  orderQuestionsForQuiz(questions, options).forEach((question, index) => {
    const questionId = String(question?._id ?? question?.id ?? `question-${index}`);

    if (!seenIds.has(questionId)) {
      seenIds.add(questionId);
      uniqueQuestions.push(question);
    }
  });

  return uniqueQuestions.slice(0, Math.max(0, Number(requestedCount || 0)));
};

export const getReviewProgress = (questions = []) => {
  const counts = (Array.isArray(questions) ? questions : []).map(getQuestionViewCount);
  const total = counts.length;
  const initiallyCompleted = counts.filter((count) => count >= 1).length;

  if (!total || !initiallyCompleted) {
    return {
      total,
      initiallyCompleted,
      initialPercentage: total ? 0 : 0,
      completedPasses: 0,
      currentPass: 1,
      currentCompleted: initiallyCompleted,
      currentPercentage: total ? Math.round((initiallyCompleted / total) * 1000) / 10 : 0,
      hasStarted: false,
    };
  }

  const completedPasses = Math.min(...counts);
  const currentPass = completedPasses + 1;
  const currentCompleted = counts.filter((count) => count >= currentPass).length;

  return {
    total,
    initiallyCompleted,
    initialPercentage: Math.round((initiallyCompleted / total) * 1000) / 10,
    completedPasses,
    currentPass,
    currentCompleted,
    currentPercentage: Math.round((currentCompleted / total) * 1000) / 10,
    hasStarted: true,
  };
};
